/**
 * Picks the dashboard greeting: what language to say hello in, and which of that language's
 * phrases to use right now.
 *
 * Everything here is pure — the browser-dependent bits (time zone, language list, current
 * time) are passed in, so the whole thing is testable and can be driven from a mockup page
 * with fabricated inputs.
 */

import GREETINGS, { GreetingVariant, LanguageGreetings, TimeBucket } from 'constants/greetings';
import { COUNTRY_LANGUAGES, TIMEZONE_COUNTRY } from 'constants/localeRegions';
import TIMEZONES from 'constants/timezones';

export type ResolvedGreeting = {
  /** The phrase, in the local language. */
  text: string;
  /** Latin transliteration, when the script isn't Latin. */
  roman?: string;
  /** English gloss — "Good morning". */
  meaning: string;
  /** BCP-47 tag for the `lang` attribute. */
  tag: string;
  /** English name of the language, for the tooltip. */
  language: string;
  /** ISO-3166 alpha-2 we inferred, when we managed to infer one. */
  country?: string;
  rtl: boolean;
  bucket: TimeBucket;
  /** True when we fell through to English rather than identifying a local language. */
  isFallback: boolean;
};

export type GreetingSignals = {
  /** IANA zone, e.g. `Europe/Berlin`. */
  timeZone?: string | null;
  /** Browser language list, most preferred first, e.g. `['de-CH', 'en-US']`. */
  languages?: readonly string[];
  /** Local hour, 0–23. */
  hour: number;
  /** Anything stable-per-person — rotates the phrase without flickering on re-render. */
  seed?: string;
};

const DEFAULT_LANGUAGE = 'en';

/**
 * Bucket boundaries, in local hours. Night runs 22:00–04:59 and is a real bucket rather than
 * a wrap-around of evening: several languages have a distinct late greeting, and the ones
 * that don't fall back to their evening phrase.
 */
export function getTimeBucket(hour: number): TimeBucket {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  if (hour >= 18 && hour < 22) return 'evening';
  return 'night';
}

/**
 * Region subtag of a BCP-47 tag: `de-CH` -> `CH`, `zh-Hant-TW` -> `TW`, `en` -> undefined.
 *
 * The leading subtag is skipped — it is the language, and it is also two letters, so a naive
 * scan reads `de-CH` as Germany.
 */
export function regionFromLocale(locale: string): string | undefined {
  const region = locale
    .split('-')
    .slice(1)
    .find((part) => /^[A-Za-z]{2}$/.test(part));
  return region ? region.toUpperCase() : undefined;
}

/** Primary language subtag, normalised: `de-CH` -> `de`, `PT-br` -> `pt`. */
export function languageFromLocale(locale: string): string {
  return locale.split('-')[0].toLowerCase();
}

/**
 * Chinese is the one case where the language subtag alone isn't enough — mainland and
 * Singapore read simplified, Taiwan and Hong Kong traditional.
 */
function normaliseChinese(locale: string): string {
  const upper = locale.toUpperCase();
  if (upper.includes('HANT') || upper.includes('-TW') || upper.includes('-HK')) return 'zh-Hant';
  return 'zh-Hans';
}

function toGreetingKey(locale: string): string | undefined {
  const language = languageFromLocale(locale);
  if (language === 'zh') return normaliseChinese(locale);
  if (language === 'no' || language === 'nn') return 'nb';
  if (language === 'tl') return 'fil';
  return GREETINGS[language] ? language : undefined;
}

/**
 * Where is this person, as best we can tell? Time zone first — it says where the body is,
 * which is what a local greeting is about — then the region subtag of the browser locale.
 */
export function resolveCountry(signals: GreetingSignals): string | undefined {
  const { timeZone, languages = [] } = signals;
  if (timeZone && TIMEZONE_COUNTRY[timeZone]) return TIMEZONE_COUNTRY[timeZone];

  const fromLocale = languages.map(regionFromLocale).find(Boolean);
  return fromLocale;
}

/**
 * Which language do we greet in?
 *
 * The country decides, because the greeting is about place: an American in Berlin gets
 * "Guten Morgen", which is the whole charm of the thing. In a multilingual country the
 * browser's language list breaks the tie. With no country at all we fall back to the
 * browser's own language, and finally to English.
 */
export function resolveLanguage(signals: GreetingSignals): {
  key: string;
  country?: string;
  isFallback: boolean;
} {
  const { languages = [] } = signals;
  const country = resolveCountry(signals);
  const candidates = country ? COUNTRY_LANGUAGES[country] : undefined;

  if (candidates && candidates.length) {
    if (candidates.length > 1) {
      const preferred = languages.map(toGreetingKey).find((key) => key && candidates.includes(key));
      if (preferred) return { key: preferred, country, isFallback: preferred === DEFAULT_LANGUAGE };
    }
    const key = candidates.find((candidate) => GREETINGS[candidate]) || DEFAULT_LANGUAGE;
    return { key, country, isFallback: key === DEFAULT_LANGUAGE };
  }

  const fromBrowser = languages.map(toGreetingKey).find(Boolean);
  if (fromBrowser) {
    return { key: fromBrowser, country, isFallback: fromBrowser === DEFAULT_LANGUAGE };
  }

  return { key: DEFAULT_LANGUAGE, country, isFallback: true };
}

/** FNV-1a. Small, stable across engines, and good enough to spread a handful of phrases. */
export function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * Builds the pool of phrases for a bucket. Time-appropriate phrases are listed twice so they
 * carry roughly half the pool — "Good morning" should be the common case, with "Hey" and the
 * regional extras as the occasional change of pace.
 */
export function buildPool(
  entry: LanguageGreetings,
  bucket: TimeBucket,
  country?: string,
): GreetingVariant[] {
  const timed = entry[bucket] || entry.evening;
  const regional = (country && entry.regional?.[country]) || [];
  return [...timed, ...timed, ...entry.anytime, ...regional];
}

/**
 * The whole decision, start to finish.
 *
 * `seed` is what makes the greeting "switch up": same seed, same phrase, so React re-renders
 * don't shuffle it mid-session, but a new day or a new time-of-day bucket deals a new one.
 */
export function resolveGreeting(signals: GreetingSignals): ResolvedGreeting {
  const bucket = getTimeBucket(signals.hour);
  const { key, country, isFallback } = resolveLanguage(signals);
  const entry = GREETINGS[key] || GREETINGS[DEFAULT_LANGUAGE];
  const pool = buildPool(entry, bucket, country);
  const variant = pool[hashSeed(signals.seed || `${bucket}`) % pool.length];

  return {
    text: variant.text,
    roman: variant.roman,
    meaning: variant.meaning,
    tag: entry.tag,
    language: entry.label,
    country,
    rtl: Boolean(entry.rtl),
    bucket,
    isFallback,
  };
}

/*
 * ---------------------------------------------------------------------------
 * Browser-side signal gathering
 * ---------------------------------------------------------------------------
 */

/** The browser's IANA zone. Returns undefined where `Intl` can't tell us (very old Safari). */
export function detectTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (error) {
    return undefined;
  }
}

export function detectLanguages(): string[] {
  if (typeof navigator === 'undefined') return [];
  const { languages, language } = navigator;
  if (languages && languages.length) return Array.from(languages);
  return language ? [language] : [];
}

/**
 * The saved-timezone fallback. `user.settings.timeZoneText` is the only location-ish thing we
 * store server-side; each entry in the TIMEZONES table carries the IANA zones it covers, so we
 * can borrow the first one. Coarse — an offset window spans many countries — but it keeps the
 * greeting working for someone whose browser won't report a zone, and it is the hook an SSR or
 * email version would use.
 */
export function ianaFromTimeZoneText(timeZoneText?: string | null): string | undefined {
  if (!timeZoneText) return undefined;
  const match = TIMEZONES.find((zone) => zone.text === timeZoneText);
  return match?.utc?.find((zone) => TIMEZONE_COUNTRY[zone]) || match?.utc?.[0];
}

/** Current hour in a given IANA zone, falling back to the device clock. */
export function localHourIn(timeZone?: string, now: Date = new Date()): number {
  if (!timeZone) return now.getHours();
  try {
    const hour = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hour12: false,
      timeZone,
    }).format(now);
    return Number(hour) % 24;
  } catch (error) {
    return now.getHours();
  }
}

/** `2026-08-20` in the person's own zone — the day part of the rotation seed. */
export function localDayKey(timeZone?: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    }).format(now);
  } catch (error) {
    return now.toISOString().slice(0, 10);
  }
}
